import {
    reauthenticateWithCredential,
    EmailAuthProvider,
    updatePassword as updateFirebasePassword,
    signInWithEmailAndPassword,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    MultiFactorResolver,
    MultiFactorError,
    AuthError,
    User,
    ApplicationVerifier,
    PhoneInfoOptions,
    MultiFactorSession,
    MultiFactorAssertion,
    Auth,
    RecaptchaVerifier,
    MultiFactorUser,
    PhoneMultiFactorEnrollInfoOptions,
    multiFactor
} from 'firebase/auth';

// Extend the User type to include multiFactor property
declare module 'firebase/auth' {
    interface User {
        multiFactor: {
            enrolledFactors: Array<{
                uid: string;
                phoneNumber?: string;
                displayName?: string;
                enrollmentTime?: Date;
                factorId: string;
            }>;
            getSession(): Promise<MultiFactorSession>;
            enroll(assertion: MultiFactorAssertion, displayName?: string | null): Promise<void>;
            unenroll(factorUid: string): Promise<void>;
        };
    }
}
import { getFirestore, doc, updateDoc, arrayRemove, arrayUnion, getDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { auth } from '@/lib/firebase';

declare global {
    interface Window {
        recaptchaVerifier: ApplicationVerifier;
        confirmationResult: any;
        mfaUser?: MultiFactorUser;
    }
}

// Type for enrolled MFA factors
export interface MultiFactorInfo {
    uid: string;
    phoneNumber?: string;
    displayName: string;
    enrollmentTime: string;
    factorId: string;
}

type UpdatePasswordParams = {
    currentPassword: string;
    newPassword: string;
};

export const updatePassword = async (newPassword: string): Promise<void> => {
    const user = auth.currentUser;

    if (!user || !user.email) {
        throw new Error('No authenticated user found');
    }

    try {
        // Save the current user's email before updating password
        const userEmail = user.email;

        // Update the password
        await updateFirebasePassword(user, newPassword);

        // After password update, the current session is invalidated
        // We need to sign the user back in with the new password
        // This requires the user's email and new password
        await signInWithEmailAndPassword(auth, userEmail, newPassword);

        return Promise.resolve();
    } catch (error: any) {
        console.error('Error updating password:', error);

        // If the error is due to token expiration, we need to handle it gracefully
        if (error.code === 'auth/user-token-expired' || error.code === 'auth/requires-recent-login') {
            // The user needs to sign in again
            await auth.signOut();
            // Rethrow with a more user-friendly message
            throw new Error('Your session has expired. Please sign in again with your new password.');
        }

        throw new Error(error.message || 'Failed to update password');
    }
};

export const verifyPhoneNumber = async (phoneNumber: string): Promise<string> => {
    console.log('Sending verification to:', phoneNumber);
    try {
        const user = auth.currentUser;

        if (!user) {
            throw new Error('No authenticated user found');
        }

        // Initialize the reCAPTCHA verifier if it doesn't already exist
        if (!window.recaptchaVerifier) {
            try {
                // Check if container exists, if not create it
                let container = document.getElementById('recaptcha-container');
                if (!container) {
                    // Create container if it doesn't exist
                    container = document.createElement('div');
                    container.id = 'recaptcha-container';
                    document.body.appendChild(container);
                    console.log('Created new reCAPTCHA container');
                } else if (container.innerHTML.trim() !== '') {
                    // If container exists but has content, clear and recreate it to avoid render errors
                    console.log('reCAPTCHA container exists but has content, recreating it');
                    container.remove();
                    container = document.createElement('div');
                    container.id = 'recaptcha-container';
                    document.body.appendChild(container);
                }
                
                const verifier = new RecaptchaVerifier(
                    auth,
                    document.getElementById('recaptcha-container')!,
                    {
                        'size': 'invisible',
                        'callback': () => {
                            console.log('reCAPTCHA verified');
                        },
                        'expired-callback': () => {
                            console.warn('reCAPTCHA expired');
                        },
                        'error-callback': (error: Error) => {
                            console.error('reCAPTCHA error:', error);
                        }
                    }
                ) as unknown as { render: () => Promise<void> } & ApplicationVerifier;
                window.recaptchaVerifier = verifier as unknown as ApplicationVerifier;
                
                try {
                    await verifier.render();
                } catch (renderError) {
                    console.error('Error rendering reCAPTCHA:', renderError);
                    // If there was an error rendering, clean up and throw error
                    if ((verifier as any).clear) {
                        (verifier as any).clear();
                    }
                    delete window.recaptchaVerifier;
                    throw new Error('Could not render verification widget. Please refresh and try again.');
                }
            } catch (error) {
                console.error('Error initializing reCAPTCHA verifier:', error);
                throw new Error('Failed to initialize security verification. Please refresh the page and try again.');
            }
        } else {
            console.log('Using existing reCAPTCHA verifier');
        }

        try {
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            
            // First try to get the MFA session
            try {
                // DED-1520: Always get a fresh multiFactor instance to avoid stale sessions
                // Don't reuse window.mfaUser as it may have an expired session
                const multiFactorInstance = multiFactor(user);
                window.mfaUser = multiFactorInstance; // Store for later use in enroll
                
                const multiFactorSession = await multiFactorInstance.getSession();
                // Persist the MFA session globally for later use in enroll
                (window as any).mfaSession = multiFactorSession;
                
                const phoneInfoOptions: PhoneInfoOptions = {
                    phoneNumber,
                    session: multiFactorSession
                };

                return await phoneAuthProvider.verifyPhoneNumber(
                    phoneInfoOptions,
                    window.recaptchaVerifier,
                );
            } catch (sessionError: any) {
                // If we can't get an MFA session, try without it (for initial setup)
                if (sessionError.code === 'auth/multi-factor-auth-required' || 
                    sessionError.code === 'auth/requires-recent-login') {
                    
                    // Try with just the phone number (for initial setup)
                    return await phoneAuthProvider.verifyPhoneNumber(
                        phoneNumber,
                        window.recaptchaVerifier
                    );
                }
                throw sessionError;
            }
        } catch (error: any) {
            console.error('Error in verifyPhoneNumber:', error);
            // Propagate the original error
            throw error;
        }
    } catch (error: any) {
        console.error('Error sending verification code:', error);

        // Propagate Firebase errors so UI can handle them
        if (error.code) throw error;

        throw new Error(error.message || 'Failed to send verification code');
    }
};

/**
 * Unenrolls a second factor after verifying the user's identity
 * @param factorUid The UID of the factor to unenroll
 * @param verificationCode Optional verification code if MFA is required
 * @param verificationId Optional verification ID if MFA is required
 */
export const unenroll2FA = async (
    factorUid: string, 
    verificationCode?: string,
    verificationId?: string
): Promise<void> => {
    const user = auth.currentUser;

    if (!user) {
        throw new Error('No authenticated user found');
    }

    try {
        console.log(`Attempting to unenroll 2FA factor: ${factorUid}`);
        if (verificationCode && verificationId) {
            console.log(`Using verification code and ID: ${verificationId.substring(0, 5)}...`);
        }
        
        const mfs = window.mfaUser || multiFactor(user);
        
        // Get the enrolled factors
        const enrolledFactors = mfs.enrolledFactors || [];
        const factorToRemove = enrolledFactors.find(factor => factor.uid === factorUid);

        if (!factorToRemove) {
            throw new Error('2FA factor not found');
        }
        
        // If verification code is provided, verify it first
        if (verificationCode && verificationId) {
            try {
                console.log('Creating phone auth credential...');
                // Create a phone auth credential with the verification ID and code
                const phoneAuthCredential = PhoneAuthProvider.credential(
                    verificationId, 
                    verificationCode
                );
                
                console.log('Creating multi-factor assertion...');
                // Create a multi-factor assertion from the credential
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
                
                console.log('Calling unenroll with factor UID:', factorToRemove.uid);
                // Use the assertion with the factor to unenroll
                await mfs.unenroll(factorToRemove);
                console.log('Successfully unenrolled with verification');
            } catch (error: any) {
                console.error('Error in verification during unenroll:', error);
                
                // Check for code expired errors
                if (error.code === 'auth/code-expired') {
                    throw new Error('Verification code has expired. Please request a new code.');
                }
                
                throw error;
            }
        } else {
            // Try to unenroll directly (will fail if MFA verification is required)
            await mfs.unenroll(factorToRemove);
        }

        console.log('Successfully unenrolled 2FA factor:', factorUid);

        // Optionally update Firestore to reflect the change
        try {
            const db = getFirestore(getApp());
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                'security.mfa.lastUpdated': new Date().toISOString()
            });
        } catch (dbError) {
            console.warn('Could not update Firestore MFA status:', dbError);
            // Non-critical error, continue
        }

    } catch (error: any) {
        console.error('Error unenrolling 2FA:', error);

        // Handle specific error cases
        if (error.code === 'auth/requires-recent-login') {
            throw new Error('recent-login-required');
        } else if (error.code === 'auth/security-constraint') {
            throw new Error('Cannot remove the last second factor. Please add another factor first.');
        } else if (error.code === 'auth/multi-factor-auth-required') {
            // This error should be handled by the UI to show MFA verification
            throw error;
        }

        throw new Error(error.message || 'Failed to unenroll 2FA factor');
    }
};

// export const verify2FACode = async (verificationId: string, verificationCode: string): Promise<MultiFactorAssertion> => {
//     try {
//         // Create a credential with the verification ID and code
//         const credential = PhoneAuthProvider.credential(verificationId, verificationCode);

//         // Create a multi-factor assertion
//         const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);

//         return multiFactorAssertion;
//     } catch (error: any) {
//         console.error('Error verifying 2FA code:', error);

//         // Handle specific error cases
//         if (error.code === 'auth/invalid-verification-code') {
//             throw new Error('Invalid verification code. Please try again.');
//         } else if (error.code === 'auth/code-expired') {
//             throw new Error('Verification code has expired. Please request a new one.');
//         } else if (error.code === 'auth/too-many-requests') {
//             throw new Error('Too many attempts. Please try again later.');
//         }

//         throw new Error(error.message || 'Failed to verify 2FA code');
//     }
// };

export const enroll2FA = async (
    phoneNumber: string,
    verificationId: string,
    verificationCode: string
): Promise<void> => {
    const user = auth.currentUser;

    if (!user) {
        throw new Error('No authenticated user found');
    }

    console.log(`Attempting to enroll 2FA for: ${phoneNumber}`);
    console.log(`Using verification ID: ${verificationId.substring(0, 5)}... and code length: ${verificationCode.length}`);

    try {
        // First check if we have a current session
        if (!(window as any).mfaSession) {
            console.log('No MFA session found, getting a fresh one...');
            try {
                // Try to get a fresh MFA session
                const session = await (window.mfaUser || multiFactor(user)).getSession();
                (window as any).mfaSession = session;
                console.log('Fresh MFA session obtained');
            } catch (sessionError: any) {
                console.error('Error getting MFA session:', sessionError);
                // Continue without session - this might work for initial enrollment
            }
        } else {
            console.log('Using existing MFA session');
        }
        
        const multiFactorUser = window.mfaUser || multiFactor(user);
        window.mfaUser = multiFactorUser;
        
        console.log('Creating phone credential from verification ID and code...');
        // Create phone credential from the verification ID and code
        const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
        
        console.log('Generating multi-factor assertion...');
        // Generate the multi-factor assertion from the credential
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
        
        console.log('Enrolling with multi-factor assertion...');
        // Enroll with the assertion and phone number as display name
        await multiFactorUser.enroll(multiFactorAssertion, phoneNumber);
        console.log('Successfully enrolled 2FA');
        
        // Clear any persisted session after success
        if ((window as any).mfaSession) {
            console.log('Clearing persisted MFA session');
            delete (window as any).mfaSession;
        }

        // Also clear the verification ID
        if (window.verificationId) {
            console.log('Clearing verification ID');
            delete window.verificationId;
        }

        console.log('Successfully enrolled 2FA for:', phoneNumber);
    } catch (error: any) {
        console.error('Error enrolling 2FA:', error);

        // Clear session on error to force a fresh session next time
        if ((window as any).mfaSession) {
            console.log('Clearing MFA session due to error');
            delete (window as any).mfaSession;
        }

        // Handle specific error cases
        if (error.code === 'auth/second-factor-already-in-use') {
            throw new Error('This phone number is already enrolled as a second factor.');
        } else if (error.code === 'auth/maximum-second-factor-count-exceeded') {
            throw new Error('Maximum number of second factors reached. Please remove one before adding another.');
        } else if (error.code === 'auth/requires-recent-login') {
            throw new Error('recent-login-required');
        } else if (error.code === 'auth/code-expired' || error.message?.includes('expired') || 
                  (error.message && error.message.toLowerCase().includes('session expired'))) {
            // Also clear verification ID on code expiry
            if (window.verificationId) {
                delete window.verificationId;
            }
            throw new Error('Verification code has expired. Please request a new code.');
        }

        throw new Error(error.message || 'Failed to enroll 2FA');
    }
};

// Helper function to get enrolled MFA factors
export const getEnrolledFactors = (): Promise<MultiFactorInfo[]> => {
    const user = auth.currentUser;

    if (!user) {
        console.log('No authenticated user');
        return Promise.resolve([]);
    }

    const mfs = multiFactor(user);

    if (mfs?.enrolledFactors) {
        return Promise.resolve(mfs.enrolledFactors.map(factor => {
            // For phone factors, the phone number is in the factor object
            const phoneNumber = 'phoneNumber' in factor ? factor.phoneNumber : 
                            (factor as any).phoneNumber || '';
                            
            return {
                uid: factor.uid,
                phoneNumber: phoneNumber,
                displayName: factor.displayName || 'Phone',
                enrollmentTime: factor.enrollmentTime ?
                    (typeof factor.enrollmentTime === 'string' ? factor.enrollmentTime :
                        (factor.enrollmentTime as any).toDate ? (factor.enrollmentTime as any).toDate().toISOString() :
                            new Date().toISOString()) :
                    new Date().toISOString(),
                factorId: factor.factorId
            };
        }));
    }
    // Force a reload of the user to get fresh MFA data
    return user.reload()
        .then(() => {
            const currentUser = auth.currentUser;
            if (!currentUser?.multiFactor) {
                console.log('No multi-factor data available');
                return [];
            }

            console.log('Enrolled factors:', currentUser.multiFactor.enrolledFactors);
            
            return currentUser.multiFactor.enrolledFactors.map(factor => {
                // For phone factors, the phone number is in the factor object
                const phoneNumber = 'phoneNumber' in factor ? factor.phoneNumber : 
                                (factor as any).phoneNumber || '';
                
                return {
                    uid: factor.uid,
                    phoneNumber: phoneNumber,
                    displayName: factor.displayName || 'Phone',
                    enrollmentTime: factor.enrollmentTime ?
                        (typeof factor.enrollmentTime === 'string' ? factor.enrollmentTime :
                            (factor.enrollmentTime as any).toDate ? (factor.enrollmentTime as any).toDate().toISOString() :
                                new Date().toISOString()) :
                        new Date().toISOString(),
                    factorId: factor.factorId
                };
            });
        })
        .catch(error => {
            console.error('Error refreshing user data:', error);
            return [];
        });
};

// Helper function to handle MFA required errors
export const handleMFARequired = async (
    error: MultiFactorError,
    phoneNumber: string,
    verifier: ApplicationVerifier
): Promise<MultiFactorAssertion> => {
    // Type assertion to access the resolver property
    const resolver = (error as any).resolver;

    if (!resolver) {
        throw new Error('Multi-factor authentication required but no resolver available');
    }

    // Send verification code to the second factor
    const phoneInfoOptions: PhoneInfoOptions & { multiFactorHint: any } = {
        multiFactorHint: (resolver as any).hints[0],
        session: (resolver as any).session
    };

    // Use the verifier to get the verification ID
    // Using any to bypass TypeScript error for verifyPhoneNumber
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await (phoneAuthProvider as any).verifyPhoneNumber(
        phoneInfoOptions,
        verifier
    );

    // In a real implementation, you would show a dialog to enter the verification code
    // and then call resolver.resolveSignIn() with the code
    return new Promise((resolve, reject) => {
        // This is a simplified example - in a real app, you would show a dialog
        // and call resolve() when the user enters the code
        const code = prompt('Enter the verification code sent to your phone:');
        if (!code) {
            reject(new Error('Verification code is required'));
            return;
        }

        const credential = PhoneAuthProvider.credential(verificationId, code);
        const assertion = PhoneMultiFactorGenerator.assertion(credential);
        resolve(assertion);
    });
};
